import { normalizeWriterPlan } from "../config/writerPlans";

const MODES = Object.freeze({
  reader: "reader",
  writer: "writer",
});

const normalizeRoleString = (value) => (value ? String(value).trim() : "");

/** Stored role or Auth custom claim. `admin` and `superadmin` are the same console. */
export const isAdminRoleString = (value) => {
  const role = normalizeRoleString(value).toLowerCase();
  return role === "admin" || role === "superadmin";
};

const normalizeRoles = (dbUserData) => {
  const rolesRaw = dbUserData?.roles;
  if (Array.isArray(rolesRaw) && rolesRaw.length > 0) {
    return rolesRaw.map((r) => normalizeRoleString(r)).filter(Boolean);
  }

  const legacyRole = normalizeRoleString(dbUserData?.role) || MODES.reader;
  return [legacyRole];
};

const normalizeSubscriptions = (dbUserData, roles) => {
  const subsRaw = dbUserData?.subscriptions;
  if (subsRaw && typeof subsRaw === "object") {
    return {
      reader: !!subsRaw.reader,
      writer: !!subsRaw.writer,
      writerPlan: normalizeWriterPlan(subsRaw.writerPlan),
    };
  }

  const legacyRole = normalizeRoleString(dbUserData?.role);
  const isWriter = legacyRole.toLowerCase() === MODES.writer || roles.some((r) => r.toLowerCase() === MODES.writer);
  return {
    reader: true,
    writer: !!isWriter,
    writerPlan: normalizeWriterPlan(undefined),
  };
};

export const resolveCurrentMode = ({ uid, dbUserData, subscriptions }) => {
  const dbMode = normalizeRoleString(dbUserData?.currentMode);
  const lsKey = uid ? `lw:currentMode:${uid}` : null;
  const lsMode = lsKey ? normalizeRoleString(localStorage.getItem(lsKey)) : "";
  const rawMode = (dbMode || lsMode || MODES.reader).toLowerCase();

  const candidate = rawMode === MODES.writer ? MODES.writer : MODES.reader;
  const allowed = candidate === MODES.writer ? !!subscriptions?.writer : !!subscriptions?.reader;
  const finalMode = allowed ? candidate : MODES.reader;

  if (lsKey) localStorage.setItem(lsKey, finalMode);
  return finalMode;
};

/**
 * Build the app's normalized user model from Firebase Auth + DB user data.
 * Keeps legacy `role` in sync for existing guards and navigation.
 */
export const buildAppUser = ({ firebaseUser, dbUserData, tokenClaims = {} }) => {
  const roles = normalizeRoles(dbUserData);
  const subscriptions = normalizeSubscriptions(dbUserData, roles);
  const currentMode = resolveCurrentMode({
    uid: firebaseUser?.uid,
    dbUserData,
    subscriptions,
  });

  const legacyRole = normalizeRoleString(dbUserData?.role) || MODES.reader;
  const claimRole = normalizeRoleString(tokenClaims?.role);
  const isAdminClaim = tokenClaims?.admin === true || isAdminRoleString(claimRole);
  const isAdminData = isAdminRoleString(legacyRole);
  const isAdmin = isAdminClaim || isAdminData;
  const writerPlan = normalizeWriterPlan(subscriptions?.writerPlan);
  return {
    uid: firebaseUser?.uid,
    email: firebaseUser?.email,
    displayName: firebaseUser?.displayName,
    photoURL: firebaseUser?.photoURL,
    emailVerified: firebaseUser?.emailVerified,
    phoneNumber: dbUserData?.phoneNumber || "",
    gender: dbUserData?.gender || "",
    // New model
    roles,
    subscriptions,
    writerPlan,
    currentMode: isAdmin ? "admin" : currentMode,
    // Canonical app role. Database `role` is not rewritten from this object.
    role: isAdmin ? "superadmin" : currentMode,
    authorName: dbUserData?.authorName || "",
    sidebarLogoUrl: dbUserData?.sidebarLogoUrl || "",
    writerTheme: dbUserData?.writerTheme && typeof dbUserData.writerTheme === "object" ? dbUserData.writerTheme : null,
    subscriptionBilling:
      dbUserData?.subscriptionBilling && typeof dbUserData.subscriptionBilling === "object"
        ? dbUserData.subscriptionBilling
        : null,
    purchases: dbUserData?.purchases || {},
  };
};

